/**
 * 1.8.2 — one New at the top of Events (D-1.8.2-4). Elijah, 8 Oct: "When I click New, I get options of what I'm trying
 * to do — add an event, add an announcement, send out a survey. If it's an event, what kind: a monthly offering, a
 * one-time event, an RSVP, a raffle."
 *
 * 25 Oct 2026 (D-1025-EVENTS): the kinds are In-house · Outing · Raffle · Calendar only (shared/event-kind-1025.ts), kept in
 * the event's own file. "Needs volunteers" goes with any of them. From there it is the 1.7.9 New event, with the
 * numbers (guests, household extra, Served) left for Edit; Repeat stays editable.
 *
 * Who sees what: each of the three is shown only to the roles that may make it today — the API's own gates (events,
 * announcements and surveys are each made by `ACCESS.staff`); a role that may make none sees no New.
 */
import type { EventKindChoice } from './event-cards';
import { repeatChoices } from './event-calendar';
import { EVENT_KINDS_1025, kindStart, type EventKind1025 } from './event-kind-1025';
import { ACCESS, type Role } from './roles';

/** Step 1: What do you want to do? */
export const NEW_THINGS = ['event', 'announcement', 'survey'] as const;
export type NewThing = (typeof NEW_THINGS)[number];

/**
 * An event → What kind of event? (25 Oct 2026, D-1025-EVENTS: In-house · Outing · Raffle · Calendar only — the older
 * Monthly offering / One-time / RSVP / Raffle list is gone; a repeat is chosen on the next screen, as it always could be.)
 */
export const NEW_EVENT_KINDS = EVENT_KINDS_1025;
export type NewEventKind = EventKind1025;

/** What New event opens with. */
export interface NewEventPreset {
  kind: EventKind1025;
  choice: EventKindChoice;
  repeat: 'monthly' | 'none';
  rule: 'everyone' | 'raffle';
  /** null = the sheet's own default (in the building). */
  offSite: boolean | null;
  /** Starts on "No limit" (in-house); an outing and a raffle start on a number. */
  noLimit: boolean;
  /** Wheelchair spots the sheet starts with (an outing starts with 2; null = none counted). */
  wheelchairSpots: number | null;
}

/** The one mapping: a kind (and the tick) onto the 1.7.9 kind, the spot rule, where it is and what the numbers start as. */
export function presetOfKind(kind: NewEventKind, volunteers: boolean): NewEventPreset {
  const s = kindStart(kind);
  return {
    kind,
    choice: { answer: s.answer, volunteers },
    repeat: 'none',
    rule: s.rule,
    offSite: s.offSite,
    noLimit: s.noLimit,
    wheelchairSpots: kind === 'outing' ? 2 : null,
  };
}

/** "Every month" for an event on this day: on its nth weekday (the 2nd Tuesday), or on the last where it is a 5th. */
export const monthlyRepeatFor = (dayKey: string): 'monthlyNth' | 'monthlyLast' => (repeatChoices(dayKey).includes('monthlyNth') ? 'monthlyNth' : 'monthlyLast');

/** Who may make each of the three today (the API's gates: POST /api/events/new, /api/announcements, /api/surveys/new). */
export const NEW_RIGHTS: Readonly<Record<NewThing, readonly Role[]>> = {
  event: ACCESS.staff,
  announcement: ACCESS.staff,
  survey: ACCESS.staff,
};

/** What New offers this role, in order; empty: no New at all. */
export function newChoicesFor(role: Role | null | undefined, rights: Readonly<Record<NewThing, readonly Role[]>> = NEW_RIGHTS): NewThing[] {
  if (!role) return [];
  return NEW_THINGS.filter((t) => rights[t].includes(role));
}
